import { useTranslation } from "react-i18next";

import type { CcReadyGate } from "@/api/code-companion";
import { useCcReadyGate } from "@/hooks/useCodeCompanion";
import { tp } from "@/lib/plural";

/**
 * Gate de prontidão, read-only (CC.34 F5).
 *
 * Diz o que falta para o pacote poder ser promovido, **antes** de alguém clicar. Até
 * aqui a única forma de descobrir era tentar promover e ler a recusa, o que obriga a
 * tela a tratar um erro como se fosse informação.
 *
 * O veredito vem inteiro do servidor, do mesmo cálculo que o `markReady` aplica. A
 * tela não reimplementa a regra de propósito: reimplementar faria o painel mentir no
 * dia em que o gate mudasse, e é justamente na promoção que a mentira sai cara.
 */

const TONE = {
  ok: { bg: "#f3faf6", bd: "#d3ecdf", fg: "#2b8f57" },
  block: { bg: "#fdf2f2", bd: "#f3d3d5", fg: "#b02a31" },
};

/** Quantos objetos barram a promoção. Exportado para poder ser testado puro. */
export function blockedCount(gate: Pick<CcReadyGate, "objects">): number {
  return gate.objects.filter((o) => !o.ok).length;
}

/**
 * Tudo que barra a promoção, e não só os objetos.
 *
 * O selo dizia `0 of 5 blocked` num pacote que o gate barrava: a contagem olhava
 * `gate.objects` e regressão não é objeto — ela bloqueia o pacote inteiro, por isso
 * vem numa lista à parte. O painel se contradizia com a própria linha logo abaixo
 * dele, e a contradição fica no lugar mais caro possível, que é o veredito.
 */
export function gateBlockers(
  gate: Pick<CcReadyGate, "objects" | "regressions">,
): { objects: number; regressions: number; total: number } {
  const objects = gate.objects.filter((o) => !o.ok).length;
  // `blocking`, não `!ok`: os dois deixaram de ser opostos quando a retirada de escopo
  // passou a existir. Um caso retirado não provou nada (`ok: false`) e também não barra
  // — contá-lo aqui faria o selo anunciar um bloqueio que o servidor não impõe.
  const regressions = gate.regressions.filter((r) => r.blocking).length;
  return { objects, regressions, total: objects + regressions };
}

/**
 * Resumo de uma linha. Pacote vazio é bloqueio, não "tudo certo": zero de zero
 * objetos aprovados não é um pacote pronto.
 */
export function gateSummary(gate: CcReadyGate): string {
  if (gate.ok) return "ready";
  if (!gate.objects.length) return "empty";
  return "blocked";
}

interface Props {
  projectId: string;
  packageId: number;
}

export default function GatePanel({ projectId, packageId }: Props) {
  const { t } = useTranslation();
  const { data: gate, isLoading, isError } = useCcReadyGate(projectId, packageId);

  if (isError) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("Could not read the readiness gate.")}
      </div>
    );
  }

  if (isLoading || !gate) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-3 text-xs text-muted-foreground">
        {t("Checking readiness...")}
      </div>
    );
  }

  const blocked = gateBlockers(gate);
  const summary = gateSummary(gate);

  return (
    <div
      data-testid="gate-panel"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Readiness gate")}
        </span>
        <div className="flex-1" />
        <span
          data-testid="gate-verdict"
          className="rounded px-2 py-0.5 font-mono text-[10px] font-semibold uppercase"
          style={
            gate.ok
              ? { background: TONE.ok.bg, color: TONE.ok.fg }
              : { background: TONE.block.bg, color: TONE.block.fg }
          }
        >
          {summary === "ready" && t("ready to promote")}
          {summary === "empty" && t("no objects in development scope")}
          {/* Quando só a regressão barra, contar objetos daria "0 of 5 blocked" num
              pacote bloqueado. Cada origem diz o que é, em vez de virar uma fração
              que não fecha. */}
          {summary === "blocked" && blocked.objects > 0 &&
            t("{{n}} of {{total}} blocked", {
              n: blocked.objects,
              total: gate.objects.length,
            })}
          {summary === "blocked" && blocked.objects === 0 && blocked.regressions > 0 &&
            tp(
              t,
              blocked.regressions,
              "1 regression blocks",
              "{{n}} regressions block",
            )}
          {summary === "blocked" && blocked.total === 0 && t("blocked")}
        </span>
      </div>

      {gate.objects.length > 0 && (
        <ul className="divide-y divide-border">
          {gate.objects.map((o) => (
            <li
              key={o.object_name}
              className="flex flex-wrap items-baseline gap-2 px-3 py-1.5 text-xs"
            >
              <span
                aria-hidden
                className="font-mono text-[11px]"
                style={{ color: o.ok ? TONE.ok.fg : TONE.block.fg }}
              >
                {o.ok ? "✓" : "✗"}
              </span>
              <span className="font-mono text-[11px]">{o.object_name}</span>
              {/* O canal muda o que o gate cobra (CC.30), então dizer qual é evita a
                  pergunta "por que esse não pede change set?". Só aparece quando não
                  é o caminho comum. */}
              {o.channel !== "abap" && (
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
                  {o.channel}
                </span>
              )}
              {o.reason && (
                <span className="text-[11px]" style={{ color: TONE.block.fg }}>
                  {o.reason}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {gate.regressions.some((r) => r.blocking) && (
        <ul className="divide-y divide-border border-t border-border">
          {/* Paridade barra o pacote inteiro, não um objeto: por isso fica fora da lista
              acima. E cada estado tem um conserto diferente — mostrar o valor da coluna
              (`pending`, `drift`) mandava o dev repetir o que não podia funcionar. */}
          {gate.regressions
            .filter((r) => r.blocking)
            .map((r) => (
              <li key={r.case_id} className="flex flex-wrap items-baseline gap-2 px-3 py-1.5">
                <span aria-hidden className="font-mono text-[11px]" style={{ color: TONE.block.fg }}>
                  ✗
                </span>
                <span className="font-mono text-[11px]">{r.case_id}</span>
                {r.reason && (
                  <span className="text-[11px]" style={{ color: TONE.block.fg }}>
                    {r.reason}
                  </span>
                )}
                {r.remedy && (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    {r.remedy === "run_gates" ? t("run the gates") : t("fix the object")}
                  </span>
                )}
              </li>
            ))}
        </ul>
      )}

      {gate.warnings.length > 0 && (
        <ul
          className="space-y-1 border-t px-3 py-2"
          style={{ borderColor: "rgba(242,115,37,.3)", background: "rgba(242,115,37,.06)" }}
        >
          {/* Aparecem também com o gate verde: é aí que importam. Um pacote promovido
              sem prova de comportamento é decisão legítima, e a tela que a esconde faz o
              dossiê parecer mais forte do que é. */}
          {gate.warnings.map((w) => (
            <li key={w} className="text-[11px]" style={{ color: "#8a4b00" }}>
              {w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
